import React from 'react';
import {
  formatHours,
  isNearCapacity,
  type WorkloadPreviewMap,
} from '../utils/taskWorkloadCapacity';
import PersonAvatar from './people/PersonAvatar';

interface Props {
  employeeIds: string[];
  previews: WorkloadPreviewMap;
  requestedHours?: number;
  getEmployeeName: (employeeId: string) => string;
}

export function TaskWorkloadPreviewList({
  employeeIds,
  previews,
  requestedHours,
  getEmployeeName,
}: Props) {
  const ids = Array.from(new Set(employeeIds.filter(Boolean)));
  if (ids.length === 0) return null;

  return (
    <div className="mt-3 space-y-2" data-testid="task-workload-preview">
      {ids.map((employeeId) => {
        const state = previews[employeeId] || { status: 'loading' as const };
        const employeeName = getEmployeeName(employeeId);
        const person = (
          <span className="inline-flex items-center gap-1.5 font-medium">
            <PersonAvatar userId={employeeId} employeeId={employeeId} name={employeeName} size={20} />
            {employeeName}
          </span>
        );

        if (state.status === 'loading' || state.status === 'idle') {
          return (
            <div key={employeeId} className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700">
              {person}
              <div>Checking employee workload...</div>
            </div>
          );
        }

        if (state.status === 'error') {
          return (
            <div key={employeeId} className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {person}
              <div>{state.error}</div>
            </div>
          );
        }

        const preview = state.data;
        const overCapacity = requestedHours !== undefined && requestedHours > preview.remaining_hours;
        const nearCapacity = !overCapacity && isNearCapacity(preview);
        const toneClass = overCapacity || preview.non_working_reason
          ? 'border-red-200 bg-red-50 text-red-700'
          : nearCapacity
          ? 'border-amber-200 bg-amber-50 text-amber-700'
          : 'border-green-200 bg-green-50 text-green-700';

        return (
          <div key={employeeId} className={`rounded-lg border px-3 py-2 text-xs ${toneClass}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              {person}
              <span>
                {formatHours(preview.office_hours)} capacity · {formatHours(preview.assigned_hours)} assigned · {formatHours(preview.remaining_hours)} available
              </span>
            </div>
            {preview.non_working_reason && (
              <div className="mt-1">
                Workload cannot be calculated because this is a non-working day: {preview.non_working_reason}.
              </div>
            )}
            {!preview.non_working_reason && overCapacity && (
              <div className="mt-1">
                Cannot assign {formatHours(requestedHours)}. Only {formatHours(preview.remaining_hours)} is available.
              </div>
            )}
            {nearCapacity && (
              <div className="mt-1">
                Near capacity for the selected task date.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default TaskWorkloadPreviewList;
